import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { settingsHref } from '../pages/settings/routing'
import {
  deriveSubsystems, rollup, useOpsStatus,
  SEV_DOT, SEV_LABEL, type Sev,
} from '../lib/opsStatus'

/**
 * Header system-status dot — System health's one-glance summary, mounted
 * inside SystemHealthControl's dropdown. Driven by the SAME shared snapshot +
 * derivation as Settings › System health (lib/opsStatus.ts), so the dot and
 * the page can never disagree. Click → that tab (admin-only; a non-admin
 * lands on Users with a one-line notice).
 */
export default function SystemStatusDot() {
  const { s } = useOpsStatus()
  const { sev, issueCount } = useMemo(() => {
    if (!s) return { sev: 'unknown' as Sev, issueCount: 0 }
    const subs = deriveSubsystems(s)
    return { sev: rollup(subs), issueCount: subs.reduce((n, x) => n + x.issues.length, 0) }
  }, [s])

  const tooltip = sev === 'green'
    ? 'System: all systems go — open System health'
    : `System: ${SEV_LABEL[sev]}${issueCount ? ` · ${issueCount} issue${issueCount > 1 ? 's' : ''}` : ''} — open System health`

  return (
    // NOTE: testid is namespaced "-legacy" (item 11, 2026-08) because this
    // component is now also mounted verbatim inside SystemHealthControl's
    // dropdown, which owns the bare "system-status-dot" testid on its own
    // outer chip — two nodes sharing one testid while the dropdown is open
    // would break any future getByTestId query. data-sev is UNCHANGED so any
    // check keyed on that attribute (rather than the id) still matches.
    <Link to={settingsHref('system')} title={tooltip} data-testid="system-status-dot-legacy" data-sev={sev}
          className="flex items-center gap-1.5 px-2 py-1 border border-gray-200 rounded bg-white text-xs text-gray-700 hover:bg-gray-50">
      <span className={`inline-block w-2 h-2 rounded-full ${SEV_DOT[sev]}`} />
      <span>System</span>
      {issueCount > 0 && sev !== 'green' && (
        <span className="text-[10px] text-gray-500">{issueCount}</span>
      )}
    </Link>
  )
}
